import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import { useToast } from '../../hooks/useToast';
import { PublicLayout } from '../../components/layout/PublicLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { LogOut, User, Mail, Phone, MapPin, Save, ArrowLeft } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { supabase } from '../../lib/supabase';
import DeviceSyncComponent from '../../components/shop/DeviceSyncComponent';
import { useTenantContext } from '../../context/TenantContext';

// Mapping d'icônes pour personnalisation - Utilise toutes les icônes Lucide
const iconMap = LucideIcons as Record<string, React.ElementType>;

function hexToRgba(hex: string, alpha = 1) {
  const cleaned = hex.replace('#', '');
  const bigint = parseInt(cleaned.length === 3
    ? cleaned.split('').map((c) => c + c).join('')
    : cleaned, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const fadeIn = { hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } } };

interface CustomerProfile {
  id: number;
  email: string;
  full_name?: string;
  phone?: string;
  address?: string;
  preferences?: Record<string, any>;
}

const CustomerProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { tenant, previewConfig } = useTenantContext();

  // Thème Shop (preview > tenant)
  const shopTheme = React.useMemo(() => {
    const base = (previewConfig?.theme?.notFound) ?? (tenant?.config?.theme?.notFound) ?? ({} as any);
    const themeColors = {
      c1: (tenant?.config?.color1 as string) || '#fe8d17', // primaire (bouton)
      c2: (tenant?.config?.color2 as string) || '#008aef', // secondaire (bouton)
      c3: (tenant?.config?.color3 as string) || '#ffffff',
      c4: (tenant?.config?.color4 as string) || '#9ca3af',
    };
    const defaults = {
      overlay: {
        enabled: true,
        useGradient: true,
        orientation: 'to-b', // to-b, to-t, to-r, to-l, to-tr, to-br, to-tl, to-bl
        color1: '#08062b',
        color2: '#08062b',
        color3: '#08062b',
        opacity1: 0.95,
        opacity2: 0.80,
        opacity3: 0.95,
      },
      icons: [
        { enabled: true, icon: 'BookOpen', color: themeColors.c1, size: 32 }, // top-left
        { enabled: true, icon: 'Sparkles',   color: themeColors.c2, size: 28 }, // top-right
        { enabled: true, icon: 'Diamond',    color: themeColors.c2, size: 24 }, // bottom-left
        { enabled: true, icon: 'Star',       color: themeColors.c1, size: 30 }, // bottom-right
      ]
    } as any;
    return {
      overlay: {
        enabled: base.overlay?.enabled ?? defaults.overlay.enabled,
        useGradient: base.overlay?.useGradient ?? defaults.overlay.useGradient,
        orientation: base.overlay?.orientation ?? defaults.overlay.orientation,
        color1: base.overlay?.color1 ?? defaults.overlay.color1,
        color2: base.overlay?.color2 ?? defaults.overlay.color2,
        color3: base.overlay?.color3 ?? defaults.overlay.color3,
        opacity1: base.overlay?.opacity1 ?? defaults.overlay.opacity1,
        opacity2: base.overlay?.opacity2 ?? defaults.overlay.opacity2,
        opacity3: base.overlay?.opacity3 ?? defaults.overlay.opacity3,
      },
      icons: (base.icons && Array.isArray(base.icons) && base.icons.length === 4)
        ? base.icons.map((i: any, idx: number) => ({
            enabled: i?.enabled ?? defaults.icons[idx].enabled,
            icon: i?.icon ?? defaults.icons[idx].icon,
            color: i?.color ?? defaults.icons[idx].color,
            size: Number(i?.size ?? defaults.icons[idx].size),
          }))
        : defaults.icons,
    };
  }, [tenant, previewConfig]);
  
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [profile, setProfile] = useState<CustomerProfile | null>(null);
  
  // Formulaire
  const [formData, setFormData] = useState({
    full_name: '',
    phone: '',
    address: '',
    email: ''
  });
  
  // Vérifier si l'utilisateur est déjà authentifié
  useEffect(() => {
    const checkAuthentication = async () => {
      const token = localStorage.getItem('customer_access_token');
      if (token) {
        try {
          const { data, error } = await supabase
            .from('shop_customer_profiles')
            .select('*')
            .eq('access_token', token)
            .single();
          
          if (error) throw error;
          
          if (data) {
            setIsAuthenticated(true);
            setProfile(data);
            setFormData({
              full_name: data.full_name || '',
              phone: data.phone || '',
              address: data.address || '',
              email: data.email || ''
            });
          } else {
            // Token invalide ou expiré
            localStorage.removeItem('customer_access_token');
          }
        } catch (error) {
          console.error('Erreur lors de la vérification du token:', error);
          localStorage.removeItem('customer_access_token');
        } finally {
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    };
    
    checkAuthentication();
  }, []);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleLogout = () => {
    localStorage.removeItem('customer_access_token');
    setIsAuthenticated(false);
    setProfile(null);
    
    toast({
      title: 'Déconnexion réussie',
      description: 'Vous avez été déconnecté avec succès.',
      variant: 'default'
    });
    
    navigate('/boutique/commandes');
  };
  
  const handleSaveProfile = async () => {
    if (!profile || !profile.id) return;
    
    setIsSaving(true);
    
    try {
      const { error } = await supabase
        .from('shop_customer_profiles')
        .update({
          full_name: formData.full_name,
          phone: formData.phone,
          address: formData.address,
          updated_at: new Date().toISOString()
        })
        .eq('id', profile.id);
      
      if (error) throw error;
      
      toast({
        title: 'Profil mis à jour',
        description: 'Vos informations ont été enregistrées avec succès.',
        variant: 'default'
      });
      
      // Mettre à jour le profil local
      setProfile({
        ...profile,
        full_name: formData.full_name,
        phone: formData.phone,
        address: formData.address
      });
    } catch (error) {
      console.error('Erreur lors de la mise à jour du profil:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de mettre à jour votre profil. Veuillez réessayer.',
        variant: 'destructive'
      });
    } finally {
      setIsSaving(false);
    }
  };
  
  return (
    <PublicLayout>
      <SEOMetaTags 
        title="Mon Profil Client" 
        description="Gérez vos informations personnelles et vos préférences."
      />
      <div className="text-white relative overflow-hidden pt-20 min-h-screen">
        {/* Fond avec la nouvelle palette de couleurs SIS */}
        <div 
          className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
          style={{
            backgroundImage: tenant?.config?.bg ? `url(${tenant.config.bg})` : 'url(https://images.pexels.com/photos/326503/pexels-photo-326503.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2)',
            backgroundAttachment: 'fixed'
          }}
        >
          {shopTheme.overlay.enabled && (
            <div className="absolute inset-0 backdrop-blur-[2px]" style={{
              backgroundImage: shopTheme.overlay.useGradient
                ? `linear-gradient(${(
                    {
                      'to-b': 'to bottom',
                      'to-t': 'to top',
                      'to-r': 'to right',
                      'to-l': 'to left',
                      'to-tr': 'to top right',
                      'to-br': 'to bottom right',
                      'to-tl': 'to top left',
                      'to-bl': 'to bottom left',
                    } as Record<string, string>
                  )[shopTheme.overlay.orientation] || 'to bottom'},
                    ${hexToRgba(shopTheme.overlay.color1, shopTheme.overlay.opacity1)},
                    ${hexToRgba(shopTheme.overlay.color2, shopTheme.overlay.opacity2)},
                    ${hexToRgba(shopTheme.overlay.color3, shopTheme.overlay.opacity3)}
                  )`
                : undefined,
              backgroundColor: !shopTheme.overlay.useGradient
                ? hexToRgba(shopTheme.overlay.color1, shopTheme.overlay.opacity2)
                : undefined,
            }} />
          )}
        </div>

        {/* Particules flottantes avec couleurs personnalisables */}
        <div className="absolute inset-0 overflow-hidden z-10">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full blur-3xl animate-pulse" style={{ backgroundColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.08) }} />
          <div className="absolute top-3/4 right-1/4 w-96 h-96 rounded-full blur-3xl animate-pulse" style={{ backgroundColor: hexToRgba(tenant?.config?.color2 || '#008aef', 0.08), animationDelay: '2s' }} />
          <div className="absolute top-1/2 left-3/4 w-48 h-48 rounded-full blur-2xl animate-pulse" style={{ backgroundColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.15), animationDelay: '4s' }} />
        </div>
        
        {/* Particules flottantes avec couleurs et icônes personnalisables */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Top-Left */}
          {shopTheme.icons[0]?.enabled && (
            <motion.div
              className="absolute top-20 left-10"
              style={{ color: hexToRgba(shopTheme.icons[0].color, 0.2) }}
              animate={{ y: [-20, 20, -20], rotate: [0, 180, 360] }}
              transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[shopTheme.icons[0].icon] || iconMap.BookOpen;
                return IconComponent ? <IconComponent size={shopTheme.icons[0].size} /> : null;
              })()}
            </motion.div>
          )}
          {/* Top-Right */}
          {shopTheme.icons[1]?.enabled && (
            <motion.div
              className="absolute top-40 right-20"
              style={{ color: hexToRgba(shopTheme.icons[1].color, 0.2) }}
              animate={{ y: [20, -20, 20], rotate: [360, 180, 0] }}
              transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[shopTheme.icons[1].icon] || iconMap.Sparkles;
                return IconComponent ? <IconComponent size={shopTheme.icons[1].size} /> : null;
              })()}
            </motion.div>
          )}
          {/* Bottom-Left */}
          {shopTheme.icons[2]?.enabled && (
            <motion.div
              className="absolute bottom-40 left-20"
              style={{ color: hexToRgba(shopTheme.icons[2].color, 0.2) }}
              animate={{ y: [-15, 15, -15], rotate: [0, 90, 180] }}
              transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[shopTheme.icons[2].icon] || iconMap.Diamond;
                return IconComponent ? <IconComponent size={shopTheme.icons[2].size} /> : null;
              })()}
            </motion.div>
          )}
          {/* Bottom-Right */}
          {shopTheme.icons[3]?.enabled && (
            <motion.div
              className="absolute bottom-20 right-10"
              style={{ color: hexToRgba(shopTheme.icons[3].color, 0.2) }}
              animate={{ y: [15, -15, 15], rotate: [180, 270, 360] }}
              transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
            >
              {(() => {
                const IconComponent = iconMap[shopTheme.icons[3].icon] || iconMap.Star;
                return IconComponent ? <IconComponent size={shopTheme.icons[3].size} /> : null;
              })()}
            </motion.div>
          )}
        </div>
        <div className="relative z-20 container mx-auto px-4 py-12 max-w-2xl">
          <motion.div 
            className="space-y-8"
            initial="hidden"
            animate="visible"
            variants={fadeIn}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/boutique/commandes')}
                  className="mr-4"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Retour
                </Button>
                <h1 className="text-2xl font-bold" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Mon Profil</h1>
              </div>
              
              {isAuthenticated && (
                <Button 
                  variant="outline" 
                  onClick={handleLogout}
                  className="transition-all duration-200"
                  style={{
                    backgroundColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.2),
                    borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.3),
                    color: tenant?.config?.color1 || '#fe8d17'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.3);
                    e.currentTarget.style.color = tenant?.config?.color3 || '#ffffff';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.2);
                    e.currentTarget.style.color = tenant?.config?.color1 || '#fe8d17';
                  }}
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Déconnexion
                </Button>
              )}
            </div>
            
            {isLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="relative">
                  <div className="w-16 h-16 border-4 rounded-full animate-spin"
                    style={{
                      borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.2),
                      borderTopColor: tenant?.config?.color1 || '#fe8d17'
                    }}
                  ></div>
                  <div className="absolute inset-0 w-16 h-16 border-4 border-transparent rounded-full animate-spin" 
                    style={{ 
                      borderTopColor: hexToRgba(tenant?.config?.color2 || '#008aef', 0.3),
                      animationDirection: 'reverse',
                      animationDuration: '1.5s'
                    }}
                  ></div>
                </div>
              </div>
            ) : isAuthenticated && profile ? (
              <>
                <Card className="bg-white/5 border backdrop-blur-sm text-white"
                  style={{
                    borderColor: `${tenant?.config?.color1 || '#fe8d17'}33`
                  }}
                >
                  <CardHeader>
                    <CardTitle style={{ color: tenant?.config?.color3 || '#ffffff' }}>Informations personnelles</CardTitle>
                    <CardDescription style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.7) }}>
                      Mettez à jour vos informations personnelles et préférences
                    </CardDescription>
                  </CardHeader>
                  
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <Label htmlFor="full_name" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Nom complet</Label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.5) }} />
                          <Input
                            id="full_name"
                            name="full_name"
                            value={formData.full_name}
                            onChange={handleInputChange}
                            className="bg-white/10 border-white/20 placeholder:text-white/50 focus:ring-2 focus:border-transparent outline-none transition-all duration-300 pl-10"
                            style={{
                              color: tenant?.config?.color3 || '#ffffff',
                              '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                            } as React.CSSProperties}
                            placeholder="Votre nom complet"
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <Label htmlFor="email" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Email</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.5) }} />
                          <Input
                            id="email"
                            name="email"
                            value={formData.email}
                            disabled
                            className="bg-white/10 border-white/20 placeholder:text-white/50 focus:ring-2 focus:border-transparent outline-none transition-all duration-300 pl-10 opacity-70"
                            style={{
                              color: tenant?.config?.color3 || '#ffffff',
                              '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                            } as React.CSSProperties}
                          />
                        </div>
                      </div>
                      
                      <div className="space-y-3">
                        <Label htmlFor="phone" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Téléphone</Label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.5) }} />
                          <Input
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            className="bg-white/10 border-white/20 placeholder:text-white/50 focus:ring-2 focus:border-transparent outline-none transition-all duration-300 pl-10"
                            style={{
                              color: tenant?.config?.color3 || '#ffffff',
                              '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                            } as React.CSSProperties}
                            placeholder="Votre numéro de téléphone"
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <Label htmlFor="address" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Adresse de livraison</Label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-3 h-4 w-4" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.5) }} />
                        <Textarea
                          id="address"
                          name="address"
                          value={formData.address}
                          onChange={handleInputChange}
                          className="bg-white/10 border-white/20 placeholder:text-white/50 focus:ring-2 focus:border-transparent outline-none transition-all duration-300 pl-10 min-h-[120px]"
                          style={{
                            color: tenant?.config?.color3 || '#ffffff',
                            '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                          } as React.CSSProperties}
                          placeholder="Votre adresse de livraison complète"
                        />
                      </div>
                    </div>
                  </CardContent>
                  
                  <CardFooter>
                    <Button 
                      onClick={handleSaveProfile}
                      disabled={isSaving}
                      className="ml-auto shadow-lg hover:shadow-xl transition-all duration-300 font-bold"
                      style={{
                        backgroundColor: tenant?.config?.color1 || '#fe8d17',
                        color: tenant?.config?.color3 || '#ffffff',
                        backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`,
                        borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.3)
                      }}
                      onMouseEnter={(e) => {
                        if (!isSaving) {
                          e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#e07a10'}, ${tenant?.config?.color1 || '#fe8d17'})`;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSaving) {
                          e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`;
                        }
                      }}
                    >
                      {isSaving ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 mr-2" style={{ borderColor: tenant?.config?.color3 || '#ffffff' }}></div>
                      ) : (
                        <Save className="h-4 w-4 mr-2" />
                      )}
                      Enregistrer les modifications
                    </Button>
                  </CardFooter>
                </Card>
                
                <div className="mt-6">
                  <DeviceSyncComponent email={profile.email} />
                </div>
              </>
            ) : (
              <Card className="bg-white/5 border backdrop-blur-sm text-white"
                style={{
                  borderColor: `${tenant?.config?.color1 || '#fe8d17'}33`
                }}
              >
                <CardHeader>
                  <CardTitle style={{ color: tenant?.config?.color3 || '#ffffff' }}>Accès au profil</CardTitle>
                  <CardDescription style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.7) }}>
                    Connectez-vous pour accéder à votre profil client
                  </CardDescription>
                </CardHeader>
                
                <CardContent>
                  <div className="text-center py-8">
                    <p className="mb-4" style={{ color: hexToRgba(tenant?.config?.color3 || '#ffffff', 0.8) }}>
                      Vous devez vous connecter pour accéder à votre profil client.
                    </p>
                    <Button 
                      onClick={() => navigate('/boutique/commandes')}
                      className="shadow-lg hover:shadow-xl transition-all duration-300 font-bold"
                      style={{
                        backgroundColor: tenant?.config?.color1 || '#fe8d17',
                        color: tenant?.config?.color3 || '#ffffff',
                        backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`,
                        borderColor: hexToRgba(tenant?.config?.color1 || '#fe8d17', 0.3)
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#e07a10'}, ${tenant?.config?.color1 || '#fe8d17'})`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`;
                      }}
                    >
                      Aller à la page de connexion
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </motion.div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default CustomerProfilePage;
